"use client";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import { signIn } from "@/lib/auth-client";
import { publishSignIn } from "@/lib/session-sync";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";

const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof LoginSchema>;

export default function LoginPage() {
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    let data;
    let error;
    try {
      ({ data, error } = await signIn.email({
        email: values.email,
        password: values.password,
      }));
    } catch {
      setServerError("Unable to reach the server. Please try again.");
      return;
    }
    if (error) {
      setServerError(error.message ?? "Sign-in failed");
      return;
    }
    // Password accepted but 2FA still required — the twoFactorClient hook
    // redirects to /two-factor; do not treat this as a completed sign-in.
    if (
      data &&
      typeof data === "object" &&
      "twoFactorRedirect" in data &&
      data.twoFactorRedirect
    ) {
      return;
    }
    if (data) {
      publishSignIn();
      // Full-document navigation (not router.push) so the Better Auth client
      // re-initializes and hydrates the active organization from the freshly
      // created session. A soft client-side navigation can leave
      // useActiveOrganization() null until a manual refresh, stranding the
      // shell/dashboard on "Loading workspace…".
      window.location.assign("/dashboard");
    }
  });

  return (
    <Card>
      <div className="mb-7">
        <h1
          className="text-[24px] font-semibold text-[var(--color-on-surface)]"
          style={{ letterSpacing: "-0.025em" }}
        >
          Welcome back
        </h1>
        <p className="text-[13.5px] text-[var(--color-on-surface-variant)] mt-1.5">
          Sign in to your Scoper workspace.
        </p>
      </div>

      <form onSubmit={onSubmit} className="stagger space-y-4">
        <Input
          label="Email"
          type="email"
          {...form.register("email")}
          error={form.formState.errors.email?.message}
          autoComplete="email"
        />
        <Input
          label="Password"
          type="password"
          {...form.register("password")}
          error={form.formState.errors.password?.message}
          autoComplete="current-password"
          showPasswordToggle={true}
        />
        {serverError && (
          <p className="text-[13px] text-[var(--color-error)]">{serverError}</p>
        )}
        <Button
          type="submit"
          loading={form.formState.isSubmitting}
          className="w-full"
          size="lg"
        >
          Sign in
        </Button>
      </form>

      <div className="divider-or">or</div>

      <Link href="/magic-link" className="block">
        <Button variant="ghost" className="w-full" size="lg">
          Email me a magic link
        </Button>
      </Link>

      <div className="flex justify-between text-[13px] mt-7 text-[var(--color-on-surface-variant)]">
        <Link
          href="/forgot-password"
          className="hover:text-[var(--color-on-surface)] transition-colors"
        >
          Forgot password?
        </Link>
        <Link
          href="/register"
          className="font-medium text-[var(--color-primary)]"
        >
          Create account
        </Link>
      </div>
    </Card>
  );
}
